import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  FlatList,
  ListRenderItemInfo,
  Platform,
  StyleSheet,
  View,
  ViewToken,
} from 'react-native';

import {
  CarouselLeftArrowButton,
  CarouselRightArrowButton,
} from './carousel-arrow-buttons';
import CarouselIndicator from './carousel-indicator';
import { useActivityContext } from '../../hooks/use-activity.context';
import { useKeyPress } from '../../hooks/use-key-press';
import type { CarouselDimensions } from '../activities/lesson/tab-activity/use-tab-activity-carousel-dimensions';

interface CarouselProps {
  items: any[];
  dimensions: CarouselDimensions;
  scrollXRef: Animated.Value;
  showPrevNextButtons: boolean;
  renderFn: (info: ListRenderItemInfo<any>) => JSX.Element;
  onLastItemReached: () => void;
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
}

const viewConfigRef = {
  itemVisiblePercentThreshold: 10,
};

const marginHorizontal = Platform.OS === 'web' ? 170 : 0;

const Carousel: React.FC<CarouselProps> = ({
  items,
  dimensions,
  scrollXRef,
  showPrevNextButtons,
  renderFn,
  onLastItemReached,
  currentIndex,
  setCurrentIndex,
}) => {
  const flatListRef = useRef<FlatList>(null);
  const { containerWidth, cardWidth, cardGap, cardHeight } = dimensions;
  const { currentActivity } = useActivityContext();
  const leftPress = useKeyPress('ArrowLeft', true);
  const rightPress = useKeyPress('ArrowRight', true);

  const scroll = useCallback(
    (direction: number) => {
      flatListRef?.current?.scrollToOffset({
        animated: true,
        offset: (cardWidth + cardGap) * direction,
      });
    },
    [cardGap, cardWidth]
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    scroll(currentIndex);
  }, [scroll, currentIndex]);

  const scrollBack = useCallback(
    () => currentIndex > 0 && setCurrentIndex(currentIndex - 1),
    [currentIndex, setCurrentIndex]
  );

  const scrollForward = useCallback(
    () => currentIndex < items.length - 1 && setCurrentIndex(currentIndex + 1),
    [currentIndex, items.length, setCurrentIndex]
  );

  useEffect(() => {
    leftPress && scrollBack();
    rightPress && scrollForward();
  }, [leftPress, rightPress, scrollBack, scrollForward]);

  const onViewRef = React.useRef(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      const index = viewableItems[0]?.index;

      if (index === items.length - 1) {
        onLastItemReached();
      }
      if (index !== null && index !== undefined && Platform.OS !== 'web') {
        setCurrentIndex(index);
      }
    }
  );

  const HeaderFooter = useMemo(
    () => (
      <View
        style={{
          width: (containerWidth - cardWidth - marginHorizontal * 2) / 2,
        }}
      />
    ),
    [cardWidth, containerWidth]
  );

  const ItemSeparator = useMemo(
    () => <View style={{ width: cardGap }} />,
    [cardGap]
  );

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <View style={[styles.container, { minHeight: cardHeight + 100 }]}>
      {showPrevNextButtons && (
        <CarouselLeftArrowButton
          disabled={currentIndex === 0}
          onPress={scrollBack}
        />
      )}

      <Animated.FlatList
        style={{ marginHorizontal }}
        showsHorizontalScrollIndicator={false}
        data={items}
        extraData={currentActivity}
        keyExtractor={(_, index) => index.toString()}
        horizontal
        snapToInterval={cardWidth + cardGap}
        decelerationRate="fast"
        bounces={false}
        ref={flatListRef}
        initialNumToRender={3}
        scrollEventThrottle={16}
        ItemSeparatorComponent={() => ItemSeparator}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollXRef } } }],
          { useNativeDriver: true }
        )}
        getItemLayout={(_, index) => ({
          length: cardWidth + cardGap,
          offset: (cardWidth + cardGap) * index,
          index,
        })}
        ListHeaderComponent={HeaderFooter}
        ListFooterComponent={HeaderFooter}
        showsVerticalScrollIndicator={false}
        onViewableItemsChanged={onViewRef.current}
        viewabilityConfig={viewConfigRef}
        renderItem={renderFn}
        scrollEnabled={Platform.OS !== 'web'}
      />
      <CarouselIndicator itemCount={items.length} currentIndex={currentIndex} />
      {showPrevNextButtons && (
        <CarouselRightArrowButton
          disabled={currentIndex === items.length - 1}
          onPress={scrollForward}
        />
      )}
    </View>
  );
};

export default Carousel;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
